import { Download, RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatBytes } from '../../../../lib/formatBytes';
import { saveBlob } from '../../../evidence-management/evidence/utils/saveBlob';
import type { FilesApi } from '../casesApi';
import type { CaseAttachmentItem } from '../caseTypes';
import { AttachFilesModal } from './AttachFilesModal';

/**
 * The files under a case or a report — ONE panel for both, so the two tabs cannot disagree about
 * columns, the deleted folder or what Download does.
 *
 * Every control carries the KEY of its act and is DISABLED, with the reason, by the case's own
 * flag — the key says the account may do this kind of thing, the flag whether this case allows it.
 * Download is a FETCH handed to the browser as a file, never a link to the endpoint: the token
 * rides in a header no navigation sends, and the server answers with an attachment on purpose.
 */
export function CaseFilesPanel({
  id,
  files,
  maxBytes,
  recordLabel,
  attachPermission,
  downloadPermission,
  writeReason,
  onChanged,
}: {
  id: string;
  files: FilesApi;
  maxBytes: number;
  /** What the files are filed with, in the reader's words. */
  recordLabel: string;
  attachPermission: string;
  downloadPermission: string;
  /** Why the reader may not attach, remove or restore here — null when they may. */
  writeReason: string | null;
  /** After a file is attached, removed or put back — so a count elsewhere on the page follows. */
  onChanged?: () => void;
}) {
  const toast = useToast();

  const [showDeleted, setShowDeleted] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [confirming, setConfirming] = useState<{ kind: 'delete' | 'restore'; row: CaseAttachmentItem } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchPage = useCallback(
    (query: DataTableQuery, signal: AbortSignal) =>
      files.list({ ...query, status: showDeleted ? undefined : 'active' }, signal),
    [files, showDeleted],
  );

  const state = useDataTable<CaseAttachmentItem>({ fetchPage, pageSize: 25 });

  const download = useCallback(
    async (row: CaseAttachmentItem) => {
      try {
        const { blob, fileName } = await files.download(row.id);
        saveBlob(blob, fileName ?? row.fileName);
      } catch (caught) {
        toast.failure(caught instanceof ApiError ? caught.message : `${row.fileName} could not be downloaded.`);
      }
    },
    [files, toast],
  );

  const rowActions = useCallback(
    (row: CaseAttachmentItem): MenuItem[] => [
      {
        key: `download-${row.id}`,
        label: 'Download',
        icon: <Download className="h-4 w-4" aria-hidden="true" />,
        permission: downloadPermission,
        onClick: () => void download(row),
      },
      {
        key: `restore-${row.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: attachPermission,
        disabledReason: row.isActive ? 'This file is not deleted.' : writeReason,
        onClick: () => setConfirming({ kind: 'restore', row }),
      },
    ],
    [attachPermission, download, downloadPermission, writeReason],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'delete') {
        await files.remove(confirming.row.id);
        toast.success(`${confirming.row.fileName} was deleted.`);
      } else {
        await files.restore(confirming.row.id);
        toast.success(`${confirming.row.fileName} was restored.`);
      }

      setConfirming(null);
      state.reload();
      onChanged?.();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <DataTablePanel
        id={id}
        fill
        state={state}
        columns={COLUMNS}
        rowKey={(row) => row.id}
        searchPlaceholder="Search file name"
        onCreate={() => setAttaching(true)}
        createLabel="Attach"
        createPermission={attachPermission}
        createDisabledReason={writeReason}
        settingsItems={[
          {
            key: 'show-deleted',
            label: 'Show deleted',
            section: 'Records',
            checked: showDeleted,
            onClick: () => setShowDeleted((v) => !v),
          },
        ]}
        rowActions={rowActions}
        onDelete={(row) => setConfirming({ kind: 'delete', row })}
        deletePermission={attachPermission}
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(row) => (!row.isActive ? 'This file is already deleted.' : writeReason)}
        otherInfo={(row) => row}
        emptyMessage={`No files are attached to ${recordLabel} yet.`}
        emptySearchMessage="No file matches that search."
      />

      {attaching ? (
        <AttachFilesModal
          id={`${id}-attach`}
          files={files}
          maxBytes={maxBytes}
          recordLabel={recordLabel}
          onClose={() => setAttaching(false)}
          onSaved={(filed, done) => {
            if (done) setAttaching(false);
            toast.success(filed === 1 ? '1 file was attached.' : `${filed} files were attached.`);
            state.reload();
            onChanged?.();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'delete' ? 'Delete file' : 'Restore file'}
          danger={confirming.kind === 'delete'}
          requireConfirm
          loading={running}
          id={`${id}-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'delete'
                  ? `${confirming.row.fileName} stops appearing in this list. The file itself is kept, and it can be restored.`
                  : `${confirming.row.fileName} appears in this list again.`}
              </p>
              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: DataColumn<CaseAttachmentItem>[] = [
  { key: 'fileName', header: 'File', width: 320, sortable: true, summary: true, render: (row) => <TextCell value={row.fileName} /> },
  { key: 'contentType', header: 'Type', width: 160, render: (row) => <TextCell value={row.contentType} mono /> },
  {
    key: 'sizeBytes',
    header: 'Size',
    width: 110,
    align: 'right',
    summary: true,
    render: (row) => <TextCell value={formatBytes(row.sizeBytes)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 110,
    status: true,
    render: (row) =>
      row.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>,
  },
];
